// SPDX-License-Identifier: AGPL-3.0-only
/**
 * The i18n revision counter.
 *
 * A language switch is observable — i18next emits `languageChanged` and the
 * provider re-renders. An OVERRIDE change is not: the language is identical,
 * the resource store simply now holds different text. Nothing in React knows
 * anything happened.
 *
 * So every operation that changes what a key resolves to bumps this counter,
 * and the surfaces that render translated text subscribe to it.
 *
 * Why a re-render and not a remount: the obvious fix — keying the subtree on
 * the revision — remounts `ThemeProvider`, which owns the locale axis and
 * holds the optimistic `setPref` layer. That wipes the user's in-flight locale
 * choice (bootstrap's server value then wins and the switch silently reverts)
 * and destroys the Translations editor's own unsaved buffer on every save. A
 * plain re-render propagates new text and keeps all component state.
 */
const listeners = new Set();
let revision = 0;
/** Current revision — the `getSnapshot` for `useSyncExternalStore`. */
export function getI18nRevision() {
    return revision;
}
/** Subscribe to revision changes; returns an unsubscribe function. */
export function subscribeI18nRevision(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
/**
 * Announce that resolved text has changed. Call after applying, replacing or
 * clearing runtime overrides, and after swapping the instance.
 *
 * Listeners are isolated: one throwing subscriber must not stop the rest from
 * learning that the strings moved.
 */
export function bumpI18nRevision() {
    revision += 1;
    for (const listener of [...listeners]) {
        try {
            listener();
        }
        catch (error) {
            // Bounded, for the reason `icu-format.ts`'s handler spells out: a raw
            // error object handed to `console.*` is serialised by whatever console
            // is listening, and at least one of them builds a string large enough to
            // throw. A reporting path that throws reports nothing.
            console.error(`[i18n] revision subscriber threw: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
}
/** Test helper — reset the counter and drop every subscriber. */
export function resetI18nRevision() {
    revision = 0;
    listeners.clear();
}
//# sourceMappingURL=revision.js.map